import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../components/Navbar';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0d0d0d 0%, #1a1a1a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const SuccessCard = styled(motion.div)`
  background: #ffffff;
  border-radius: 20px;
  padding: 3rem 2.5rem;
  max-width: 600px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Icon = styled.div`
  font-size: 4rem;
  margin-bottom: 1.5rem;
  animation: bounce 1s ease-in-out;
  
  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-20px); }
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #4caf50;
  margin-bottom: 0.75rem;
  font-weight: 700;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const InfoBox = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 1.5rem;
  border-radius: 12px;
  margin: 1.5rem 0;
  border: 2px solid #e0e0e0;
`;

const InfoRow = styled.div`
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.div`
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 0.5rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Value = styled.div`
  font-size: 1.25rem;
  color: #d4af37;
  font-weight: bold;
  font-family: 'Courier New', monospace;
  letter-spacing: 1px;
  padding: 0.75rem;
  background: #ffffff;
  border-radius: 8px;
  border: 2px dashed #d4af37;
`;

const Message = styled.p`
  font-size: 0.95rem;
  color: #333;
  line-height: 1.6;
  margin: 1rem 0;
  
  strong {
    color: #4caf50;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  flex: 1;
  padding: 0.875rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  }
`;

const PrimaryButton = styled(Button)`
  background: #4caf50;
  color: white;
  
  &:hover {
    background: #45a049;
  }
`;

const SecondaryButton = styled(Button)`
  background: #ffffff;
  color: #333;
  border: 2px solid #e0e0e0;
  
  &:hover {
    border-color: #d4af37;
    color: #d4af37;
  }
`;

const OrderSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const [orderData, setOrderData] = useState<{ order_id: string; customer_id: string } | null>(null);

  useEffect(() => {
    // URL'den veya state'den sipariş bilgilerini al
    const state = location.state as { order_id: string; customer_id: string } | undefined;
    
    if (state?.order_id && state?.customer_id) {
      setOrderData(state);
    } else {
      // Eğer sipariş bilgisi yoksa anasayfaya yönlendir
      navigate('/');
    }
  }, [location, navigate]);

  if (!orderData) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{language === 'tr' ? 'Sipariş Tamamlandı - Barkın Sayın' : 'Order Completed - Barkın Sayın'}</title>
      </Helmet>
      
      <PageContainer>
        <SuccessCard
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Icon>🎉</Icon>
          <Title>{language === 'tr' ? 'Siparişiniz Başarıyla Oluşturuldu!' : 'Your Order Has Been Successfully Created!'}</Title>
          <Subtitle>
            {language === 'tr' ? 'Teşekkür ederiz! Siparişiniz alınmış ve işleme konulmuştur.' : 'Thank you! Your order has been received and is being processed.'}
          </Subtitle>
          
          <InfoBox>
            <InfoRow>
              <Label>{language === 'tr' ? 'Sipariş Numarası' : 'Order Number'}</Label>
              <Value>{orderData.order_id}</Value>
            </InfoRow>
            
            <InfoRow>
              <Label>{language === 'tr' ? 'Müşteri Kimlik Numarası' : 'Customer ID'}</Label>
              <Value>{orderData.customer_id}</Value>
            </InfoRow>
          </InfoBox>
          
          <Message>
            <strong>{language === 'tr' ? 'Bu kimlik numarasını kullanarak siparişinizin durumunu takip edebilirsiniz.' : 'You can track your order status using this ID number.'}</strong>
          </Message>
          
          <Message style={{ fontSize: '1rem', color: '#666' }}>
            {language === 'tr' ? (
              <>
                📧 Sipariş onayı ve detaylar email adresinize gönderildi.<br />
                📦 Ödemeniz onaylandıktan sonra kargoya verilecektir.<br />
                🔍 Sipariş durumunuzu müşteri kimlik numaranız ile takip edebilirsiniz.
              </>
            ) : (
              <>
                📧 Order confirmation and details have been sent to your email.<br />
                📦 Your order will be shipped after payment confirmation.<br />
                🔍 You can track your order status with your customer ID.
              </>
            )}
          </Message>
          
          <ButtonGroup>
            <PrimaryButton onClick={() => navigate('/orders')}>
              {language === 'tr' ? 'Siparişimi Takip Et' : 'Track My Order'}
            </PrimaryButton>
            <SecondaryButton onClick={() => navigate('/store')}>
              {language === 'tr' ? 'Alışverişe Devam Et' : 'Continue Shopping'}
            </SecondaryButton>
          </ButtonGroup>
        </SuccessCard>
      </PageContainer>
    </>
  );
};

export default OrderSuccess;
